import { Stack, Card, CardContent, Skeleton, Checkbox, Box } from "@mui/material";
import { DataGridPro, GridColDef, GridDensity, GridFilterModel, GridPaginationModel, GridSortModel } from "@mui/x-data-grid-pro";
import { useGetFilterdSummaryQuery, useGetTagsQuery, ClusterGridParams } from "src/context/api/tags/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useDispatch, useSelector } from "react-redux";
import { fDate } from "src/utils/format-time";
import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import {
    GridRowSelectionModel,
    useGridApiRef
} from "@mui/x-data-grid-pro";
import { useLocales } from "src/locales";
import "./datagrid/datagrid.scss";
import { FilterPanelCustom } from "src/components/data-grid/filter-panel-custom";
import { CountryBar, CountryItem } from "src/components/country-bar";
import { HEADER } from 'src/layouts/config-layout';
import { setFilterCountry } from "src/context/reducers/filter-settings/filterSlice";
import { countryFilterFor } from "src/sections/overview/dashboards/views/explore/countryLabel";
import { CountryTabLabel } from "src/sections/overview/dashboards/views/explore/CountryTabLabel";
import { EXPLORE_COLORS, PAGE_PADDING_X } from "src/sections/overview/dashboards/views/explore/constants";
import TagsCell from "./datagrid/tags-cell";
import ClusterToolbar from "./datagrid/cluster-toolbar";
import { ALL_REGIONS, ClusterManagerConfig } from "./config";
import MetricBarCell from "./datagrid/metric-bar-cell";

const useColumns = (config: ClusterManagerConfig, rows: any[], region: string, countries: CountryItem[]) => {
    const workspace = useCurrentWorkspace();
    const { currentLang } = useLocales();
    const locale = currentLang.value || "en";
    const maxByField = useMemo(
        () => Object.fromEntries(config.metricColumns.map((column) => [
            column.field,
            Math.max(0, ...rows.map((row: any) => row[column.field] || 0)),
        ])),
        [config, rows]
    );
    return [
        {
            field: 'id',
            headerName: 'Search Term',
            headerClassName: 'header-font-color',
            flex: 1,
        },
        ...config.metricColumns.map((column) => ({
            field: column.field,
            headerName: column.headerName,
            headerAlign: 'left',
            headerClassName: 'header-font-color',
            flex: 1,
            minWidth: 220,
            type: 'number',
            renderCell: (params: any) => (
                <MetricBarCell column={column} value={params.value} max={maxByField[column.field]} currency={workspace?.currency} locale={locale} />
            ),
        })),
        {
            field: 'tags',
            headerClassName: 'header-font-color',
            headerName: 'Tags',
            flex: 1,
            minWidth: 400,
            sortable: false,
            cellClassName: 'tags-cell',
            renderCell: (params: any) => (
                <TagsCell
                    tags={params.row.tags || []}
                    row={params.row}
                    module={config.module}
                    region={region}
                    regionScoped={config.regionScoped}
                    countries={countries}
                />
            ),
        },
    ] as GridColDef[];
};

type ClusterManagerProps = {
    config: ClusterManagerConfig;
    /** Tabs of the CountryBar; the list is frozen at first load. */
    countries: CountryItem[];
    isLoadingCountries: boolean;
};

export default function ClusterManager({ config, countries, isLoadingCountries }: ClusterManagerProps) {
    const workspace = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);
    const skipQuery = !filterStore.start || !filterStore.end || !workspace?.id;
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });
    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: config.defaultSortField, sort: "desc" }]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const [multiTaggable, setMultiTaggable] = useState<boolean>(false);
    const [density, setDensity] = useState<GridDensity>("standard");
    const [fullResult, selectFullResult] = useState<boolean>(false);

    const apiRef = useGridApiRef();

    const dispatch = useDispatch();

    // Liste FIGÉE au 1er chargement : useCountryData relit filterStore.countries, donc dès qu'on
    // sélectionne un pays elle se réduirait à ce seul pays et le bandeau disparaîtrait
    // (CountryBar renvoie null si <= 1 pays). On fige la liste complète (coverage % sur l'ensemble).
    const [countryList, setCountryList] = useState<CountryItem[]>([]);
    // Défaut à l'atterrissage : l'onglet « All » (vue agrégée tous pays), comme le Funnel.
    // La CountryBar utilise le sentinel 'ALL' (cf. son mode includeAll).
    const [selectedCountry, setSelectedCountry]: any = useState<string | null>(ALL_REGIONS);
    const region = selectedCountry || ALL_REGIONS;

    // On first load: freeze the full list and, for modules that opt in, apply the "All"
    // filter to the global country filter (Brand legacy behaviour).
    // The /data endpoint filters by country NAME (region_name), not code — see the
    // canonical consumer useCountryData in visibility/search-terms.tsx. countryFilterFor
    // sends the full list for "All" (an empty array would return 0 rows via IN UNNEST).
    useEffect(() => {
        if (countryList.length === 0 && countries.length > 0) {
            setCountryList(countries);
            if (config.publishesCountryFilter) {
                const allNames = countries.map((c) => c.country_name);
                dispatch(setFilterCountry(countryFilterFor('ALL', allNames)));
            }
        }
    }, [countries, countryList.length, dispatch, config.publishesCountryFilter]);

    const gridParams: ClusterGridParams = {
        workspaceId: workspace?.id as number,
        module: config.module,
        region,
        start: fDate(filterStore.start, "yyyy-MM-dd"),
        end: fDate(filterStore.end, "yyyy-MM-dd"),
        keywords: filterStore.keywords,
        devices: filterStore.devices,
        platforms: filterStore.platforms,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        filterModel,
        tags: filterStore.tags.map((tag: any) => tag.label),
        sortField: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort as string,
    };

    const { data, isLoading, isFetching, isUninitialized, refetch: refetchTags } = useGetTagsQuery(gridParams, { skip: skipQuery });
    const { data: filterdSummary } = useGetFilterdSummaryQuery(gridParams, { skip: skipQuery });

    useEffect(() => {
        if (rowSelectionModel.length > 0) {
            setMultiTaggable(true);
        } else {
            setMultiTaggable(false);
        }
    }, [rowSelectionModel]);

    useEffect(() => {
        // Leaving "select all" must not clear the selection here: the grid already emitted
        // the right model (all minus the unchecked row, or nothing for the header checkbox).
        if (!fullResult) return;

        if (isFetching || !data?.data) {
            // on attend que les données arrivent
            return;
        }

        setRowSelectionModel(data.data.map((row: any) => row.id));
        const desiredText =
            fullResult && filterdSummary?.total_search_terms != null
                ? `${filterdSummary.total_search_terms} rows selected`
                : undefined;

        const update = () => {
            const el = document.querySelector<HTMLElement>(".MuiDataGrid-selectedRowCount");
            if (!el) return false;
            if (desiredText) el.textContent = desiredText;
            return true;
        };

        // 1) Tentative immédiate (si déjà rendu)
        if (update()) return;

        // 2) Sinon, on observe le DOM jusqu’à apparition (puis on stop)
        const observer = new MutationObserver(() => {
            if (update()) observer.disconnect();
        });
        observer.observe(document.body, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, [
        fullResult,
        isFetching,
        data?.data,
    ]);

    const CustomCheckbox = forwardRef(function CustomCheckboxFactory(props: any, ref) {
        const onChange = useCallback((e: any, checked: boolean) => {
            if (e.target.name === 'select_all_rows') {
                selectFullResult(checked);
            }
            else if (checked === false) {
                selectFullResult(false);
            }
            props.onChange?.(e, checked);
        }, []);

        return (
            <Checkbox
                ref={ref}
                {...props}
                sx={{ ...props.sx, pl: 1.5 }}
                onChange={onChange}
            />
        );
    })


    const countryBarLoading =
        isLoading ||
        isFetching ||
        isUninitialized ||
        (isLoadingCountries && countryList.length === 0);

    return (
        <Stack direction="column" spacing={2} px={"24px"}>
            <Box>
                <CountryBar
                    countries={countryList}
                    selectedCountry={selectedCountry}
                    onCountryChange={setSelectedCountry}
                    includeAll
                    loading={countryList.length === 0 || countryBarLoading}
                    // +6 vs the old HEADER.H_DESKTOP*3: the secondary nav grew (title 52→54,
                    // filter bar 52→56), so the bar and its content spacer shift down to match.
                    backgroundColor="background.paper"
                    stickyTop={HEADER.H_DESKTOP * 2}
                    topGap={60}
                    reserveGapWhenHidden
                    contentPaddingX={PAGE_PADDING_X}
                    tabSpacing={12}
                    selectedColor={'#9982CC'}
                    selectedBorderWidth={2}
                    renderCountryLabel={(c, isSelected) => (
                        <CountryTabLabel country={c} isSelected={isSelected} />
                    )}
                />
                <Card sx={{
                    "& .MuiCardContent-root:last-child": { paddingBottom: "0 !important" },
                    border: "1px solid rgba(145, 158, 171, 0.20)",
                    borderRadius: "8px",
                    mt: countryList.length > 1 ? 0 : 1,
                }}>
                    <CardContent sx={{
                        px: 0,
                        py: 0,
                        paddingBottom: "24px",
                        overflow: "visible",
                        minHeight: 600, // laisse la hauteur fixe pour éviter les sauts
                        position: "relative"
                    }}>
                        {/* Skeleton superposé */}
                        {(isLoading || isFetching || isUninitialized || isLoadingCountries && countryList.length === 0) && (
                            <Skeleton
                                variant="rectangular"
                                height="100%"
                                width="100%"
                                animation="wave"
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    zIndex: 10
                                }}
                            />
                        )}
                        <DataGridPro
                            sx={{
                                height: "689px",
                                "& .MuiTablePagination-select": {
                                    border: "1px solid rgba(145, 158, 171, 0.20)",
                                    borderRadius: "6px",
                                    backgroundColor: "transparent",
                                    minWidth: 56,
                                },
                                "& .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled": {
                                    color: "#919EAB",
                                    opacity: 0.48,
                                },
                                "& .MuiTablePagination-select .MuiSelect-select": {
                                    padding: "6px 30px 6px 12px",
                                    color: "#919EAB",
                                },
    
                                "& .MuiTablePagination-select .MuiSvgIcon-root": {
                                    color: "#919EAB",
                                },
                                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                                    outline: "none",
                                    backgroundColor: "transparent",
                                },
    
                                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                                    outline: "none",
                                    backgroundColor: "transparent",
                                },
    
                                "& .MuiTablePagination-root": {
                                    color: "#919EAB",
                                },
                                "& .MuiDataGrid-cell": {
                                    borderRight: "none !important",
                                    borderBottom: "none !important",
                                },
                                "& .MuiDataGrid-columnSeparator": {
                                    display: "none !important",
                                },
    
                                "& .MuiDataGrid-row": {
                                    borderBottom: "none !important",
                                },
                                "& .MuiTablePagination-toolbar": {
                                    minHeight: 40,
                                    height: 40,
                                    color: "#919EAB",
                                    px: "24px"
                                },
    
                                "& .MuiDataGrid-footerContainer": {
                                    minHeight: "40px !important",
                                    height: "40px !important",
                                    backgroundColor: "#FAFAFA",
                                },
    
                                "& .MuiTablePagination-selectLabel, \
                                & .MuiTablePagination-displayedRows": {
                                    color: "#919EAB",
                                },
    
                                "& .MuiTablePagination-selectIcon, \
                                & .MuiTablePagination-actions button": {
                                    color: "#919EAB",
                                },
    
                                "& .MuiDataGrid-columnHeader": {
                                    backgroundColor: "#F4F5F7",
                                    borderLeft: "1px solid #e9ecee",
                                    borderRight: "none !important",
                                    borderTop: "none !important",
                                    borderBottom: "none !important",
                                },
                                opacity: (isLoading || isFetching || isUninitialized) ? 0.5 : 1,
                            }}
                            classes={{
                                root: "search-term-grid",
                                columnHeaders: "search-term-grid-column-headers",
                                columnHeader: "search-term-grid-column-header",
                            }}
                            pageSizeOptions={[20, 50, 100]}
                            columnHeaderHeight={36}
                            rowHeight={44}
                            rows={data?.data || []}
                            columns={useColumns(config, data?.data || [], region, countryList)}
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            apiRef={apiRef}
                            disableMultipleColumnsSorting={true}
                            rowSelectionModel={rowSelectionModel}
                            onRowSelectionModelChange={(newSelectionModel) => setRowSelectionModel([...newSelectionModel])}
                            disableRowSelectionOnClick
                            checkboxSelection
                            filterModel={filterModel}
                            onFilterModelChange={setFilterModel}
                            density={density}
                            onDensityChange={setDensity}
                            filterMode="server"
                            sortingMode="server"
                            sortingOrder={['desc', 'asc']}
                            sortModel={sortModel}
                            onSortModelChange={setSortModel}
                            slots={{
                                filterPanel: (props) => <FilterPanelCustom {...props} getColumnForNewFilter={(args) => args ? args.columns[0].field : undefined} />,
                                toolbar: ClusterToolbar,
                                baseCheckbox: CustomCheckbox,
                            }}
    
                            slotProps={{
                                toolbar: {
                                    fullResult,
                                    printOptions: { disableToolbarButton: true },
                                    csvOptions: { disableToolbarButton: true },
                                    filterModel,
                                    multiTaggable,
                                    rowSelectionModel,
                                    filterdSummary,
                                    refetchTags,
                                    apiRef: apiRef,
                                    rows: data?.data || [],
                                    queryParams: gridParams,
                                    regionScoped: config.regionScoped,
                                } as any,
                            }}
                            pagination
                            paginationMode="server"
                            rowCount={data?.total_rows || 0}
                        />
                    </CardContent>
                </Card>
            </Box>
        </Stack>
    );

}